<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { BroadcastMessage } from '@/types/broadcast'
import { useApi } from '@/composables/useApi'
import { formatDate24h } from '@/utils/dateFormatter'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { useCrud } from '@/composables/useCrud'
import CreateBroadcastDialog from '@/views/apps/broadcast/CreateBroadcastDialog.vue'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'BroadcastMessage',
  },
})

const { t } = useI18n()
const { handleError, handleSuccess } = useErrorHandler()

// CRUD operations for broadcast (toggle status)
const { update: updateBroadcast, saving: toggling } = useCrud('/broadcast', {
  messages: {
    updateSuccess: t('broadcast.statusUpdatedSuccessfully'),
  },
  onSuccess: async () => {
    await fetchBroadcasts()
    await loadBroadcastStats()
  },
})

// 👉 Store
const searchQuery = ref('')
const selectedStatus = ref('active')
const selectedType = ref()
const selectedPriority = ref()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const selectedRows = ref([])

const isCreateDialogVisible = ref(false)
const isEditMode = ref(false)
const broadcastToEdit = ref<BroadcastMessage | null>(null)

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = computed(() => [
  { title: t('broadcast.title'), key: 'title', sortable: true },
  { title: t('broadcast.content'), key: 'content', sortable: true },
  { title: t('broadcast.target'), key: 'target', sortable: true },
  { title: t('broadcast.sender'), key: 'sender', sortable: true },
  { title: t('broadcast.reads'), key: 'readCount', sortable: true },
  { title: t('broadcast.createdAt'), key: 'createdAt', sortable: true },
  { title: t('broadcast.status'), key: 'isActive', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false },
])

// 👉 Fetching broadcasts
const { data: broadcastsData, execute: fetchBroadcasts, isFetching: loading } = useApi<any>(createUrl('/broadcast', {
  query: {
    q: searchQuery,
    status: selectedStatus,
    type: selectedType,
    priority: selectedPriority,
    itemsPerPage,
    page,
    sortBy,
    orderBy,
  },
}))

const broadcasts = computed((): BroadcastMessage[] => broadcastsData.value?.broadcasts || [])
const totalBroadcasts = computed(() => broadcastsData.value?.totalBroadcasts || 0)

// Filter options
const status = computed(() => [
  { title: t('broadcast.filter.active'), value: 'active' },
  { title: t('broadcast.filter.inactive'), value: 'inactive' },
  { title: t('broadcast.filter.all'), value: 'all' },
])

const typeOptions = computed(() => [
  { title: t('broadcast.type.info'), value: 'info' },
  { title: t('broadcast.type.warning'), value: 'warning' },
  { title: t('broadcast.type.success'), value: 'success' },
  { title: t('broadcast.type.error'), value: 'error' },
])

const priorityOptions = computed(() => [
  { title: t('broadcast.priority.low'), value: 'low' },
  { title: t('broadcast.priority.normal'), value: 'normal' },
  { title: t('broadcast.priority.high'), value: 'high' },
  { title: t('broadcast.priority.urgent'), value: 'urgent' },
])

// Get message type color
const getMessageTypeColor = (type: string) => {
  switch (type) {
    case 'info': return 'primary'
    case 'warning': return 'warning'
    case 'success': return 'success'
    case 'error': return 'error'
    default: return 'primary'
  }
}

// Get priority color
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'low': return 'success'
    case 'normal': return 'info'
    case 'high': return 'warning'
    case 'urgent': return 'error'
    default: return 'info'
  }
}

// Open create dialog
const openCreateDialog = () => {
  isEditMode.value = false
  broadcastToEdit.value = null
  isCreateDialogVisible.value = true
}

// Open edit dialog
const openEditDialog = (item: BroadcastMessage) => {
  isEditMode.value = true
  broadcastToEdit.value = item
  isCreateDialogVisible.value = true
}

// Toggle active status (using useCrud composable)
const toggleActive = async (item: BroadcastMessage) => {
  try {
    await updateBroadcast(item.id, { isActive: !item.isActive })

    // Update local state
    item.isActive = !item.isActive
  }
  catch (error) {
    console.error('Failed to toggle broadcast status:', error)
    handleError(error, t('broadcast.error'))
  }
}

// 👉 Broadcast Statistics
const broadcastStats = ref({
  totalBroadcasts: 0,
  activeBroadcasts: 0,
  inactiveBroadcasts: 0,
  totalReads: 0,
})

// 👉 Computed widget data based on real statistics
const widgetData = computed(() => [
  {
    title: t('broadcast.stats.totalMessages'),
    value: broadcastStats.value.totalBroadcasts.toString(),
    change: 0,
    desc: t('broadcast.stats.totalMessagesDesc'),
    icon: 'tabler-message',
    iconColor: 'primary',
  },
  {
    title: t('broadcast.stats.activeMessages'),
    value: broadcastStats.value.activeBroadcasts.toString(),
    change: 0,
    desc: t('broadcast.stats.activeMessagesDesc'),
    icon: 'tabler-message-circle',
    iconColor: 'success',
  },
  {
    title: t('broadcast.stats.inactiveMessages'),
    value: broadcastStats.value.inactiveBroadcasts.toString(),
    change: 0,
    desc: t('broadcast.stats.inactiveMessagesDesc'),
    icon: 'tabler-message-off',
    iconColor: 'warning',
  },
  {
    title: t('broadcast.stats.totalReads'),
    value: broadcastStats.value.totalReads.toString(),
    change: 0,
    desc: t('broadcast.stats.totalReadsDesc'),
    icon: 'tabler-eye',
    iconColor: 'info',
  },
])

// 👉 Load broadcast statistics
const loadBroadcastStats = async () => {
  try {
    // ใช้ broadcast API สำหรับ admin statistics
    const { data } = await useApi('/broadcast/stats')

    if (data.value) {
      broadcastStats.value = {
        totalBroadcasts: data.value.totalBroadcasts || 0,
        activeBroadcasts: data.value.activeBroadcasts || 0,
        inactiveBroadcasts: data.value.inactiveBroadcasts || 0,
        totalReads: data.value.totalReads || 0,
      }
    }
  }
  catch (error) {
    console.error('Failed to load broadcast statistics:', error)
  }
}

// 👉 Watch for changes in broadcasts data to update statistics
watch(broadcasts, () => {
  if (broadcasts.value.length > 0)
    loadBroadcastStats()
}, { immediate: true })

onMounted(async () => {
  // Load initial statistics
  await loadBroadcastStats()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('broadcast.title') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('broadcast.subtitle') }}
      </p>
    </div>

    <!-- 👉 Widgets -->
    <div class="d-flex mb-6">
      <VRow>
        <template
          v-for="(data, id) in widgetData"
          :key="id"
        >
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <VCard>
              <VCardText>
                <div class="d-flex justify-space-between">
                  <div class="d-flex flex-column gap-y-1">
                    <div class="text-body-1 text-high-emphasis">
                      {{ data.title }}
                    </div>
                    <div class="d-flex gap-x-2 align-center">
                      <h4 class="text-h4">
                        {{ data.value }}
                      </h4>
                    </div>
                    <div class="text-sm">
                      {{ data.desc }}
                    </div>
                  </div>
                  <VAvatar
                    :color="data.iconColor"
                    variant="tonal"
                    rounded
                    size="42"
                  >
                    <VIcon
                      :icon="data.icon"
                      size="26"
                    />
                  </VAvatar>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </template>
      </VRow>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select Status -->
          <VCol
            cols="12"
            sm="3"
          >
            <AppSelect
              v-model="selectedStatus"
              :placeholder="t('common.selectStatus')"
              :items="status"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
          <!-- 👉 Select Type -->
          <VCol
            cols="12"
            sm="3"
          >
            <AppSelect
              v-model="selectedType"
              :placeholder="t('common.selectType')"
              :items="typeOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
          <!-- 👉 Select Priority -->
          <VCol
            cols="12"
            sm="3"
          >
            <AppSelect
              v-model="selectedPriority"
              :placeholder="t('broadcast.selectPriority')"
              :items="priorityOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-broadcast-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('broadcast.searchBroadcast')"
            />
          </div>

          <!-- 👉 Add broadcast button -->
          <VBtn
            v-if="$can('create', 'BroadcastMessage')"
            prepend-icon="tabler-plus"
            @click="openCreateDialog"
          >
            {{ t('broadcast.createMessage') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:model-value="selectedRows"
        v-model:page="page"
        :items="broadcasts"
        item-value="id"
        :items-length="totalBroadcasts"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- Title -->
        <template #item.title="{ item }">
          <div class="d-flex align-center gap-x-2">
            <VChip
              :color="getMessageTypeColor(item.type)"
              size="small"
              variant="tonal"
            >
              {{ t(`broadcast.type.${item.type}`) }}
            </VChip>
            <VChip
              :color="getPriorityColor(item.priority)"
              size="small"
              variant="outlined"
            >
              {{ t(`broadcast.priority.${item.priority}`) }}
            </VChip>
            <span class="font-weight-medium">
              {{ item.title }}
            </span>
          </div>
        </template>

        <!-- Content -->
        <template #item.content="{ item }">
          <div class="text-body-2">
            {{ item.content.length > 100 ? `${item.content.substring(0, 100)}...` : item.content }}
          </div>
        </template>

        <!-- Target -->
        <template #item.target="{ item }">
          <VChip
            :color="item.targetType === 'all' ? 'primary' : 'info'"
            size="small"
            variant="tonal"
          >
            {{ t(`broadcast.targetType.${item.targetType}`) }}
          </VChip>
        </template>

        <!-- Sender -->
        <template #item.sender="{ item }">
          <div class="d-flex align-center gap-x-2">
            <VAvatar
              size="24"
              :color="getMessageTypeColor(item.type)"
              variant="tonal"
            >
              <span class="text-xs">{{ item.sender.firstName.charAt(0) }}{{ item.sender.lastName.charAt(0) }}</span>
            </VAvatar>
            <span class="text-body-2">{{ item.sender.firstName }} {{ item.sender.lastName }}</span>
          </div>
        </template>

        <!-- Read Count -->
        <template #item.readCount="{ item }">
          <VChip
            color="info"
            size="small"
            variant="outlined"
          >
            {{ item._count?.readStatus || 0 }} {{ t('broadcast.reads') }}
          </VChip>
        </template>

        <!-- Created At -->
        <template #item.createdAt="{ item }">
          <span class="text-sm">
            {{ formatDate24h(item.createdAt) }}
          </span>
        </template>

        <!-- Status -->
        <template #item.isActive="{ item }">
          <VChip
            :color="item.isActive ? 'success' : 'secondary'"
            size="small"
            label
            class="text-capitalize"
          >
            {{ item.isActive ? t('common.active') : t('common.inactive') }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-2">
            <VBtn
              icon
              variant="text"
              color="primary"
              size="small"
              @click="openEditDialog(item)"
            >
              <VIcon icon="tabler-edit" />
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ t('common.edit') }}
              </VTooltip>
            </VBtn>
            <VBtn
              icon
              variant="text"
              :color="item.isActive ? 'warning' : 'success'"
              size="small"
              @click="toggleActive(item)"
            >
              <VIcon :icon="item.isActive ? 'tabler-eye-off' : 'tabler-eye'" />
              <VTooltip
                activator="parent"
                location="top"
              >
                {{ item.isActive ? t('common.deactivate') : t('common.activate') }}
              </VTooltip>
            </VBtn>
          </div>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalBroadcasts"
          />
        </template>
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>

    <!-- 👉 Create/Edit Broadcast Dialog -->
    <CreateBroadcastDialog
      v-model:is-dialog-visible="isCreateDialogVisible"
      :mode="isEditMode ? 'edit' : 'create'"
      :broadcast-data="broadcastToEdit"
      @broadcast-created="fetchBroadcasts"
      @broadcast-updated="fetchBroadcasts"
    />
  </section>
</template>
